import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import getLang from 'I18n/utils/getLang';
import { fetchLimitUsage } from 'pricing-pages-lib/api/fetchLimitUsage';
import { fetchUpgradeConfig } from 'pricing-pages-lib/api/fetchUpgradeConfig';
import { filterLimits, sortLimits } from 'pricing-pages-lib/utils/ebx638Utils';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { SCREENS } from 'pricing-pages-ui/constants/tracking/Locations';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { FREE, STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import Screens from '../../constants/routing/Screens';
import { useIsEBX632Variant } from '../../hooks/ebx632Hooks';
import { useIsEBX638Variant, useIsInActiveTrial } from '../../hooks/ebx638Hooks';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFeatureStore } from '../../hooks/useFeatureStore';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { useAppSelector } from '../../reducers';
import { isAppAuthSelector } from '../../selectors/AuthSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { getIsUpgradeSourceEligible } from '../../utils/ebx632Utils';
import { logExposure } from '../../utils/laboratoryClient';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import ProductInfoContainer from '../common/ProductInfoContainer';
import LimitUsageSection from '../ebx638/LimitUsageSection';
import SuiteFeatureTable from '../feature-table/SuiteFeatureTable';
import WhyGoHubSpotSection from '../free-tools-page/WhyGoHubSpotSection';
import { SuiteTiles } from '../merch-card/SuiteTiles';
import Ebx632HeroBanner from './Ebx632HeroBanner';
import SuiteStarterHeroBanner from './SuiteStarterHeroBanner';

const SuiteStarterPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('starterCrmSuite');
  const [upgradeConfig, setUpgradeConfig] = useState(); //EBX638

  const [limitUsages, setLimitUsages] = useState();
  const isEBX638Variant = useIsEBX638Variant();
  const ownedProducts = useSelector(state => {
    const hasPricingAndPurchasingSucceeded = hasPricingAndPurchasingSucceededSelector(state);
    if (!hasPricingAndPurchasingSucceeded) return [];
    return productsFromProductInventorySelector(state);
  });
  const isInTrial = useIsInActiveTrial();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const isFree = isAppAuth && ownedProducts.length <= 0;
  const isEBX632Variant = useIsEBX632Variant();
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: SCREENS.SUITE_STARTER
    });
  }, []);
  useEffect(() => {
    const {
      upgradeSource
    } = getQueryParams();
    const isUpgradeSourceEligible = getIsUpgradeSourceEligible(upgradeSource);
    const lang = getLang();

    if (isUpgradeSourceEligible && lang === 'en') {
      if (isEBX632Variant) {
        logExposure('ebx-632');
      }

      fetchUpgradeConfig(upgradeSource).then(upgConfig => {
        setUpgradeConfig(upgConfig);
      }).catch(() => {
        setUpgradeConfig(null);
      });
    } else {
      setUpgradeConfig(null);
    }
  }, [isEBX632Variant]); //EBX638

  useEffect(() => {
    if (isFree && !isInTrial) {
      logExposure('ebx-638');

      if (isEBX638Variant) {
        fetchLimitUsage().then(limitUsageResponse => {
          setLimitUsages(sortLimits(filterLimits(limitUsageResponse)));
        }).catch(() => {
          setLimitUsages(null);
        });
      } else {
        setLimitUsages(null);
      }
    }
  }, [isEBX638Variant, isFree, isInTrial]);
  const productLevels = [FREE, STARTER];

  const buildFilteredHighlightedProducts = productLine => {
    const highlightedProducts = getHighlightsForProductLine(productLine);
    return highlightedProducts.filter(apiName => {
      const localProductLevel = getProductLevelFromApiNameString(apiName);
      return productLevels.includes(localProductLevel);
    });
  };

  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts(MARKETING),
    productLine: MARKETING
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts(SALES),
    productLine: SALES
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts(SERVICE),
    productLine: SERVICE
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts(OPERATIONS),
    productLine: OPERATIONS
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts(CMS),
    productLine: CMS
  });
  const productsByProductLine = {
    [MARKETING]: buildFilteredHighlightedProducts(MARKETING),
    [SALES]: buildFilteredHighlightedProducts(SALES),
    [SERVICE]: buildFilteredHighlightedProducts(SERVICE),
    [OPERATIONS]: buildFilteredHighlightedProducts(OPERATIONS),
    [CMS]: buildFilteredHighlightedProducts(CMS)
  }; //EBX638

  const isEbx638 = !!(isEBX638Variant && limitUsages);
  const isEbx632 = !!(isEBX632Variant && upgradeConfig);
  const isLoading = isEBX632Variant === undefined || upgradeConfig === undefined;
  const isLoadingEBX638 = isEBX638Variant === undefined || limitUsages === undefined;
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_STARTER_PAGE"
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isLoading,
      heights: [705],
      widths: ['100%'],
      Content: isEbx632 ? Ebx632HeroBanner : SuiteStarterHeroBanner,
      contentProps: isEbx632 ? {
        upgradeConfig
      } : {}
    }), /*#__PURE__*/_jsxs(ProductInfoContainer, {
      style: {
        margin: 'auto'
      },
      children: [/*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading,
        Content: !isEbx632 ? SuiteTiles : undefined,
        contentProps: !isEbx632 ? {
          isSuiteStarterPage: true
        } : undefined
      }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoadingEBX638,
        Content: isEbx638 ? LimitUsageSection : undefined,
        contentProps: isEbx638 ? {
          limitUsages
        } : undefined
      }), /*#__PURE__*/_jsx(SuiteFeatureTable, {
        productsByProductLine: productsByProductLine,
        screen: Screens.SUITE_STARTER,
        isSuiteStarterPage: true
      }), /*#__PURE__*/_jsx(WhyGoHubSpotSection, {
        selectedNavItem: "suite-starter",
        containerStyles: {
          width: '100vw',
          marginBottom: 0
        }
      })]
    })]
  });
};

export default SuiteStarterPage;